<template>
  <diagnostic-section-layout
    class="diagnostic-section"
    :title="title"
    :subtitle="subtitle"
    :section-primary-button-text="sectionPrimaryButtonText"
    :section-secondary-button-text="sectionSecondaryButtonText"
    :recommended-products-title="recommendedProductsTitle"
    :serum-product="serumProduct"
    :single-product="hasOnlyOnePromotedProduct"
    @order-prescription="onToggleCart"
    @check-diagnostic="onCheckDiagnosticClick"
  >
    <template v-slot:products>
      <app-product
        v-if="hasOnlyOnePromotedProduct"
        class="diagnostic-section__single-product"
        :product-name="getProductName(recommendedProductOptionsList[0])"
      />

      <swiper-type-one
        v-else
        class="diagnostic-section__swiper"
        :items="recommendedProductOptionsList"
      >
        <template v-slot:item="{ item }">
          <app-product
            :product-name="getProductName(item)"
            :has-overlay-image="false"
            class="diagnostic-section__swiper-single-product"
          />
        </template>
      </swiper-type-one>
    </template>
  </diagnostic-section-layout>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import DiagnosticSectionLayout from '@/components/diagnostic-section/DiagnosticSectionLayout.vue';
import SwiperTypeOne from '@/components/common/app-swiper/SwiperTypeOne.vue';
import AppProduct from '@/components/common/AppProduct.vue';

import {
  getProductOptionsDefaultOption,
  getProductOptionsName
} from '@/models/core/ProductOptions';

import { isChromeBrowser } from '@/utils/common';
import { PRODUCT_NAMES } from '@/constants/treatment';

import { types } from '@/store/types';

export default {
  name: 'DiagnosticSection',
  components: { DiagnosticSectionLayout, SwiperTypeOne, AppProduct },
  props: {
    hasDiagnostic: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState({
      patientSkinData: types.state.PATIENT_SKIN_DATA,
      locale: types.state.LOCALE
    }),
    ...mapGetters({
      recommendedProductOptionsSets: types.getters.RECOMMENDED_PRODUCT_OPTIONS_SETS
    }),
    recommendedProductOptionsList() {
      return Object.values(this.recommendedProductOptionsSets);
    },
    title() {
      const { doctorName, patientName } = this.patientSkinData;

      if (!this.serumProduct) {
        const title = this.hasOnlyOnePromotedProduct
          ? 'welcomeSection.infoSection.titleNoSerumOneProduct'
          : 'welcomeSection.infoSection.titleNoSerumMoreProducts';

        return this.$t(title, [doctorName, patientName]);
      }

      return this.$t('welcomeSection.infoSection.title', [doctorName, patientName]);
    },
    subtitle() {
      const { diagnosticDate } = this.patientSkinData;

      if (!diagnosticDate) {
        return '';
      }

      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };
      const date = new Intl.DateTimeFormat(this.locale, options).format(new Date(diagnosticDate));

      return this.$t('welcomeSection.infoSection.diagnosticDate', [date]);
    },
    sectionPrimaryButtonText() {
      if (!this.serumProduct) {
        const buttonText = this.hasOnlyOnePromotedProduct
          ? 'action.orderProduct'
          : 'action.orderProducts';

        return this.$t(buttonText);
      }

      return this.$t('action.orderPrescription');
    },
    sectionSecondaryButtonText() {
      const buttonText =
        this.serumProduct && this.hasDiagnostic
          ? 'action.checkDiagnostic'
          : 'action.seeAllProducts';

      return this.$t(buttonText);
    },
    recommendedProductsTitle() {
      if (!this.serumProduct) {
        const title = this.hasOnlyOnePromotedProduct
          ? 'welcomeSection.infoSection.titleRecommendedProduct'
          : 'welcomeSection.infoSection.titleRecommendedProducts';

        return this.$t(title);
      }

      return this.$t('welcomeSection.infoSection.titleRecommendedTreatment');
    },
    serumProduct() {
      const faceSerumOptions = this.recommendedProductOptionsSets[PRODUCT_NAMES.FACE_SERUM] || null;
      const eyeSerumOptions =
        this.recommendedProductOptionsSets[PRODUCT_NAMES.EYE_CONTOUR_SERUM] || null;
      const options = faceSerumOptions || eyeSerumOptions;

      return options ? getProductOptionsDefaultOption(options) : null;
    },
    hasOnlyOnePromotedProduct() {
      return this.recommendedProductOptionsList.length === 1;
    }
  },
  methods: {
    ...mapMutations({
      onToggleCart: types.mutations.TOGGLE_OPEN_CART
    }),
    onCheckDiagnosticClick() {
      if (this.serumProduct && this.hasDiagnostic) {
        const duration = isChromeBrowser ? 200 : 700;

        this.$scrollTo('#your-skin', { duration, offset: -80 });
      } else {
        this.$scrollTo('#your-routine', { duration: 100, offset: -80 });
      }
    },
    getProductName(options) {
      return getProductOptionsName(options);
    }
  }
};
</script>

<style lang="scss" scoped>
.diagnostic-section {
  --unsafe-label-font-size: 12px;
  --unsafe-label-line-height: 15px;

  &__swiper {
    margin-inline-end: calc(-1 * var(--ws-grid-m, 16px));
  }

  &__swiper-single-product {
    height: var(--item-height);
  }
}

@media (min-width: 360px) {
  .diagnostic-section {
    &__single-product {
      --unsafe-label-font-size: 14px;
      --unsafe-label-line-height: 20px;
    }
  }
}

@media (min-width: 480px) {
  .diagnostic-section {
    --item-width: 60vw;

    &__single-product {
      width: 60vw;
    }
  }
}

@media (min-width: 600px) {
  .diagnostic-section {
    &__swiper {
      margin-inline-end: calc(
        -1 * (var(--ws-grid-col-size) + var(--ws-grid-g) + var(--ws-grid-m, 16px))
      );
    }

    &__single-product {
      width: 42vw;
    }
  }
}

@media (min-width: 960px) {
  .diagnostic-section {
    width: 100%;
    min-height: 93vh;
    padding-bottom: 90px;

    &__swiper {
      margin-inline-end: calc(-1 * var(--ws-grid-m, 16px));
    }

    &__single-product {
      width: 60%;
    }
  }
}

@media (min-width: 1100px) {
  .diagnostic-section {
    &__swiper-single-product {
      --unsafe-label-font-size: 14px;
      --unsafe-label-line-height: 20px;
    }
  }
}

@media (min-width: 1440px) {
  .diagnostic-section {
    &__swiper-single-product {
      --unsafe-label-font-size: 14px;
      --unsafe-label-line-height: 20px;
    }
  }
}
</style>
